import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import useVoiceNote from './useVoiceNote';

// "Ask about you" — a chat that can only read. Opened from a card's Ask (with
// that observation loaded) or from the Insights capture bar (general). Same
// full-screen session shell as the Log page's logger: it is a thing you enter
// deliberately, not wallpaper.
//
// Every assistant turn shows its sources underneath — which reads it made —
// because a number without its source is the one thing this product never
// shows. If the server had to cut sentences from a reply (numbers the data
// didn't say, words that claim a cause), the bubble says so rather than
// quietly looking shorter.

const MAX_HISTORY = 10;

const GENERAL_PROMPTS = [
  "What's moved in my data over the last two weeks?",
  'How has my sleep been lately?',
  "What's in my stack, and since when?",
];
const CONTEXT_PROMPTS = [
  'What else moved alongside this?',
  'How does this compare with my usual?',
  'Has this happened before?',
];

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}
       stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);
const MicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
       stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round"
          d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
  </svg>
);
const StopIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
       stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round"
          d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z" />
  </svg>
);
const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
  </svg>
);

const formatElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const Bubble = ({ m }) => {
  const mine = m.role === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-base whitespace-pre-wrap ${
          mine
            ? 'bg-cta text-cta-fg rounded-br-sm'
            : 'bg-elev text-fg rounded-bl-sm'
        }`}
      >
        {m.text}
        {!mine && m.used?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.used.map((u, i) => (
              <span
                key={`${u.tool}-${i}`}
                className="inline-flex items-center rounded-full border border-line px-2 py-0.5 text-sm text-fg-2"
              >
                Read: {u.label}
              </span>
            ))}
          </div>
        )}
        {!mine && m.trimmed && (
          <p className="mt-2 text-sm text-fg-3">
            Part of this answer was left out because it used numbers that aren't in your data,
            or words that claimed a cause.
          </p>
        )}
      </div>
    </div>
  );
};

const AskPanel = ({ open, onClose, context, initialText = '', startVoice = false }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState(initialText);
  const [sending, setSending] = useState(false);
  const [disclaimer, setDisclaimer] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const appendText = useCallback((t) => {
    setText((prev) => (prev.trim() ? `${prev.trim()} ${t}` : t));
  }, []);
  const voice = useVoiceNote(appendText);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const startedVoice = useRef(false);
  useEffect(() => {
    if (open && startVoice && !startedVoice.current && voice.canRecord) {
      startedVoice.current = true;
      voice.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startVoice]);

  // Escape closes, like any session surface.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const send = async (raw) => {
    const trimmed = (raw ?? text).trim();
    if (!trimmed || sending) return;
    if (voice.recording) voice.stop();
    const history = messages.slice(-MAX_HISTORY).map((m) => ({ role: m.role, content: m.text }));
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setText('');
    setSending(true);
    try {
      const res = await apiClient.post('/api/ask', {
        message: trimmed,
        history,
        observation_id: context?.id || null,
      }, { timeout: 180000 });
      const { reply, used, trimmed: cut, disclaimer: d } = res.data;
      if (d) setDisclaimer(d);
      setMessages((prev) => [...prev, { role: 'assistant', text: reply, used: used || [], trimmed: !!cut }]);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) toast.error(detail || 'Slow down a moment. Too many questions.');
      else if (status === 503) toast.error(detail || "Ask isn't available right now.");
      else if (status === 401) toast.error('Please log in again.');
      else toast.error(detail || 'Something went wrong. Please try again.');
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: "Sorry, I couldn't answer that just now. Mind asking again?",
        used: [],
      }]);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const prompts = context ? CONTEXT_PROMPTS : GENERAL_PROMPTS;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-canvas text-fg" role="dialog" aria-modal="true" aria-label="Ask about you">
      <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-2.5">
        <span className="text-base font-medium">Ask about you</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-9 w-9 items-center justify-center rounded-full text-fg-2 hover:bg-elev"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col">
        {context && (
          <div className="shrink-0 border-b border-line px-4 py-2.5">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 text-sm font-medium text-fg-3">{context.kind_label} ·</span>
              <p className="min-w-0 text-base font-medium text-fg">{context.headline}</p>
            </div>
          </div>
        )}

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 && !sending && (
            <div className="pt-4">
              <p className="text-base text-fg-2">
                {context
                  ? 'Ask anything about this observation. Answers come from your own readings, and each one shows what it read.'
                  : 'Ask anything about your data. Answers come from your own readings, never guessed, and each one shows what it read.'}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {prompts.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => send(p)}
                    className="rounded-full border border-line px-3 py-1.5 text-sm font-medium text-fg-2 hover:bg-elev"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => <Bubble key={i} m={m} />)}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-sm bg-elev px-4 py-3">
                <span className="flex gap-1">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-fg-3" style={{ animationDelay: '0ms' }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-fg-3" style={{ animationDelay: '150ms' }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-fg-3" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-line p-3">
          <div className="flex items-end gap-2">
            <Textarea
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={context ? 'Ask about this…' : 'Ask about your data…'}
              maxLength={1500}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              className="flex-1 resize-none !min-h-[2.75rem] max-h-28 py-2.5 !text-base leading-snug"
            />
            {voice.recording ? (
              <Button type="button" size="icon" variant="destructive" onClick={voice.stop}
                      title="Stop recording" className="h-11 w-11 shrink-0 ring-2 ring-red-500">
                <StopIcon />
              </Button>
            ) : text.trim() || !voice.canRecord ? (
              <Button type="button" size="icon" onClick={() => send()} disabled={sending || !text.trim()}
                      className="h-11 w-11 shrink-0" title="Send">
                <SendIcon />
              </Button>
            ) : (
              <Button type="button" size="icon" variant="outline" onClick={voice.start}
                      disabled={voice.transcribing} title="Dictate a question" className="h-11 w-11 shrink-0">
                <MicIcon />
              </Button>
            )}
          </div>
          {voice.recording ? (
            <span className="mt-1.5 flex items-center gap-2 text-sm font-medium text-off-strong dark:text-off">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
              </span>
              Recording {formatElapsed(voice.elapsed)}. Tap the square when you're done.
            </span>
          ) : voice.transcribing ? (
            <span className="mt-1.5 block text-sm font-medium text-fg-2">
              Writing down what you said…
            </span>
          ) : (
            <p className="mt-1.5 text-sm text-fg-3">
              {disclaimer || 'Describes your own measurements and what moved alongside them. Not medical advice.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskPanel;
